import {
  View,
  Text,
  TextInput,
  NativeModules,
  BackHandler,
  Platform,
  ScrollView,
  SafeAreaView,
  TouchableOpacity,
} from 'react-native';
import React, {useState, useRef, useMemo, useEffect} from 'react';
import NavigationBar from 'NavigationBar';
import NavigationManager from 'NavigationManager';
import {_m} from 'INTL';
import styles from './AeropayMFAStyles';
import {useMutation} from '@tanstack/react-query';
import NetClient from 'NetClient';
import {ErrorResponse} from 'NetClientTypes';
import LoadingIndicator from 'LoadingIndicator';
import SKZPersonalInfo from 'SKZPersonalInfo';
import SkillzImage from 'SkillzImage';
import PushButton from 'PushButton';
import SkillzSVG from 'SkillzSVG';
import NotificationToast from 'NotificationToast';
import NotificationNavigationController from 'NotificationNavigationController';
import {default as SkillzStyle} from 'SkillzStyle';
import AeropayAPIController from 'AeropayAPIController';
import SecureBankingDrawer from './SecureBankingDrawer'

const {tabHighlightColor} = SkillzStyle.fullStyles;

const CODE_LENGTH = 6; // length of aeropay verification code

// FIXME: this needs to be updated to the real support email address
// waiting for response about this issue (whether to hard code it or put it in a client property)
const AEROPAY_SUPPORT_EMAIL = 'support@aeropay.com';

type AeropayMFAScreenProps = {
  currentUserInfo: SKZPersonalInfo;
  onComplete: () => void;
  displayMessage: string;
  hasBackButton?: boolean;
  supportEmailAddress?: string;
};

const AeropayMFAScreen = ({
  currentUserInfo,
  onComplete,
  displayMessage,
  hasBackButton = true,
  supportEmailAddress = AEROPAY_SUPPORT_EMAIL,
}: AeropayMFAScreenProps) => {

  const [isSecurityPopupVisible, setIsSecurityPopupVisible] = useState(false);

  const securityPopUp = () => setIsSecurityPopupVisible(true);
  const handleSecurityDismiss = () => setIsSecurityPopupVisible(false);


  const [code, setCode] = useState<Array<string>>(Array(CODE_LENGTH).fill(''));
  const inputRefs = useRef<Array<TextInput | null>>([]);
  const [verificationError, setVerificationError] = useState<string>('');
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const [verificationCodeSent, setVerificationCodeSent] = useState<boolean>(false);

  const isButtonEnabled = useMemo(() => {
    return code.join('').length === CODE_LENGTH && !verificationError;
  }, [code, verificationError]);

  const onBackNavigation = () => {
    NavigationManager.pop();
    return true;
  };

  useEffect(() => {
    if (Platform.OS == 'android') {
      BackHandler.addEventListener('hardwareBackPress', onBackNavigation);
    }
    return () => {
      if (Platform.OS == 'android') {
        BackHandler.removeEventListener('hardwareBackPress', onBackNavigation);
      }
    };
  }, []);

  const handleInputChange = (text: string, index: number) => {
    const newCode = [...code];
    text.trim();
    newCode[index] = text;
    setCode(newCode);

    if (text && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
    if (!text && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    if (index === CODE_LENGTH - 1) {
      inputRefs.current[index]?.blur();
    }
  };

  type AeropayVerificationSuccess = {
    success: boolean;
    error: null;
  };

  // TODO: move to separate file (AeropayAPIController) for separation of concern
  const confirmVerificationCode = async (): Promise<AeropayVerificationSuccess> => {
    const verificationCode = code.join('');
    const body = {
      verification_code: verificationCode,
      phone_number: currentUserInfo.phone_number,
    };
    return new Promise(
      (resolve: (response: any) => void, reject: (error: ErrorResponse) => void) => {
        NetClient.postURL(
          '/payment/v1/confirm-aeropay-user',
          body,
          NativeModules.SKZAPIManager.OnError.failSilently,
          (error: ErrorResponse | null, payload?: unknown) => {
            if (error) {
              return reject(error);
            }
            if (!error && payload) {
              return resolve(payload as AeropayVerificationSuccess);
            }
          }
        );
      }
    );
  };
  const newAeropayUserResponse = async () =>
    await AeropayAPIController.createAeropayUser(currentUserInfo);

  const resendVerificationCodeMutaion = useMutation(newAeropayUserResponse, {
    onMutate: () => {
      setVerificationCodeSent(true);
      LoadingIndicator.show();
    },
    onSuccess: () => {
      NotificationNavigationController.showInAppToast(
        renderToast(false, _m('New Verification Code Sent!')),
        {
          lifetime: 3000,
          animated: true,
        }
      );
    },
    onError: () => {
      setVerificationError(_m('Incorrect verification code. Try again.'));
      NotificationNavigationController.showInAppToast(
        renderToast(true, _m('Could not resend verification code!')),
        {
          lifetime: 3000,
          animated: true,
        }
      );
    },
    onSettled: () => {
      LoadingIndicator.hide();
    },
  });

  const verifyCodeMutation = useMutation(confirmVerificationCode, {
    onMutate: () => {
      setIsVerifying(true);
      LoadingIndicator.show();
    },
    onSuccess: () => {
      NotificationNavigationController.showInAppToast(renderToast(false, _m('Account Verified!')), {
        lifetime: 3000,
        animated: true,
      });
      onComplete();
    },
    onError: () => {
      setVerificationError(_m('Incorrect verification code. Try again.'));
      NotificationNavigationController.showInAppToast(
        renderToast(true, _m('Verification Failed!')),
        {
          lifetime: 3000,
          animated: true,
        }
      );
    },
    onSettled: () => {
      LoadingIndicator.hide();
    },
  });
  const verifyEmail = async () => {
    verifyCodeMutation.mutate();
  };

  const resendVerificationCode = async () => {
    if (!verificationCodeSent) {
      resendVerificationCodeMutaion.mutate();
    }
  };

  // reset the form to the initial state to allow the user to try again.
  // resets the resend code status to allow the user to reattempt sending the code to their email again
  const dismissErrorMessage = () => {
    setIsVerifying(false);
    setVerificationCodeSent(false);
    setVerificationError('');
    setCode(Array(CODE_LENGTH).fill(''));
  };

  // special function to use with the display message in order to display the email address as a different style from the rest of the message.
  function splitString(displayMessage: string, separator: string) {
    const index = displayMessage.indexOf(separator);
    if (index === -1) {
      return [displayMessage];
    }
    const stringEnding = displayMessage.substring(index + separator.length);
    const endingWithSeparator = separator + stringEnding;

    return [displayMessage.substring(0, index), endingWithSeparator];
  }

  // index 0 === regular style message, index 1 === bold style email
  const splitDisplayMessage = splitString(displayMessage, '*');

  // opacity change for when button is disabled
  const disabledStyle = isButtonEnabled ? {} : {opacity: 0.5};

  const renderToast = (error: boolean, text: string) => (
    <NotificationToast
      toastType={'mergeAccount'}
      secondaryText={text}
      icon={error ? 'icon_x_gradient_background.svg' : 'merge_account_icon.svg'}
      onDismiss={() => {
        NotificationNavigationController.hideInAppToast();
      }}
    />
  );

  return (
    <SafeAreaView style={styles.mainContainer}>
      <NavigationBar
        hasBackButton={hasBackButton}
        onBackButtonPress={onBackNavigation}
        title={_m('Deposit')}
        hasRightComponent
        renderRightComponent={
            <TouchableOpacity onPress={securityPopUp} hitSlop={10}>
              <SkillzImage
                style={styles.shieldIcon}
                source='icon_shield_white.svg'
                tintColor='#FFFFFF'
              />
          </TouchableOpacity>
        }
      />
      <ScrollView style={{backgroundColor: '#FAFAFA'}}>
        <View style={styles.mainContainer}>
          <View style={styles.verifyEmailContainer}>
            <View style={styles.headerContainer}>
              <SkillzImage source={'email_icon.png'} style={styles.emailIcon} />
              <Text style={styles.headerTitleText} allowFontScaling={false}>{_m('Verify Your Email')}</Text>
              <Text style={styles.headerSubtitleText} allowFontScaling={false}>
                {splitDisplayMessage[0]}{' '}
                <Text style={styles.headerSubtitleBoldText} allowFontScaling={false}>{splitDisplayMessage[1]}</Text>
              </Text>
            </View>
            <View style={styles.inputFormContainer}>
              {code.map((digit, index) => (
                <TextInput
                  key={index}
                  style={
                    inputRefs.current[index]?.isFocused()
                      ? styles.inputStyleFocused
                      : styles.inputStyle
                  }
                  maxLength={1}
                  keyboardType='numeric'
                  value={digit}
                  onChangeText={(text) => handleInputChange(text, index)}
                  ref={(ref) => (inputRefs.current[index] = ref)}
                  returnKeyType='done'
                  testID={'input' + index}
                />
              ))}
            </View>
            <View style={styles.buttonContainer}>
              <PushButton
                cornerRadius={50}
                colors={[tabHighlightColor.color]}
                onPress={verifyEmail}
                style={[styles.submitButton, disabledStyle]}
                textStyle={styles.submitText}
                title={_m('VERIFY EMAIL')}
                disabled={!isButtonEnabled || isVerifying}
                accessibilityLabel={'Confirm Account Information'}
              />
              {verificationError && (
                <View>
                  <Text style={styles.verificationErrorText} allowFontScaling={false}>
                    {verificationError}{' '}
                    <Text style={styles.verificationErrorDismiss} onPress={dismissErrorMessage} allowFontScaling={false}>
                      {_m('Dismiss')}
                    </Text>
                  </Text>
                </View>
              )}
            </View>
            <View style={styles.resendCodeContainer}>
              <View style={styles.didntReceiveLine}>
                <Text style={styles.resendCodeText} allowFontScaling={false}>{_m("Didn't receive the code?")}</Text>
                <Text style={styles.resendCodeLinkText} onPress={resendVerificationCode} allowFontScaling={false}>
                  {_m(verificationCodeSent ? 'Code Sent!' : 'Resend Code')}
                </Text>
              </View>
              <Text style={styles.resendCodeText} allowFontScaling={false}>
                {_m('You can resend code in')}{' '}
                <Text style={styles.resendCodeTextBold} allowFontScaling={false}>{_m('30')}</Text>{' '}
                <Text style={styles.resendCodeText} allowFontScaling={false}>{_m('seconds')}</Text>
              </Text>
            </View>
            <View>
              <SkillzSVG style={styles.aeropayIcon} source='secured_by_aeropay.svg' />
            </View>
          </View>
        </View>
      </ScrollView>
      <View style={styles.supportSection}>
        <Text style={styles.somethingWrongText} allowFontScaling={false}>{_m('Something Wrong? Please reach out to')}</Text>
        <Text style={styles.supportEmail} allowFontScaling={false}>{supportEmailAddress}</Text>
      </View>
      <SecureBankingDrawer 
        visible={isSecurityPopupVisible}
        onDismiss={handleSecurityDismiss}
      />
    </SafeAreaView>
  );
};

export default AeropayMFAScreen;
